import {
  Autocomplete,
  FormControlLabel,
  Checkbox,
  TextField,
  Grid,
  FormLabel,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import useTemplates from "../../../pages/facility/hooks/useTemplates";
import { useMedicalConfig } from "../../../slices/configuration";
import { EncounterType } from "../../../types/medicalEncounter";
import templateApi from "../../../api/medicals/templateApi";
import { EncounterTemplate } from "../../../types/templates";
import {
  setPreferredDetailedTemplate,
  setPreferredSimpleTemplate,
} from "../../../slices/template";

type TemplateSelectProps = {
  disabled?: boolean;
  encounterType: EncounterType;
  fieldSize?: "small" | "medium";
  onTemplateSelect: (template?: EncounterTemplate) => void;
  selectedTemplateId: string;
};

export default function TemplateSelect({
  disabled,
  encounterType,
  fieldSize = "medium",
  onTemplateSelect,
  selectedTemplateId,
}: TemplateSelectProps) {
  const medicalConfig = useMedicalConfig();
  const dispatch = useDispatch();
  const {
    soapTemplates,
    detailedTemplates,
    preferredSimpleTemplate,
    preferredDetailedTemplate,
  } = useTemplates();
  const [futureUse, setFutureUse] = useState(false);

  const options =
    encounterType === "simple" ? soapTemplates : detailedTemplates;

  const selected = useMemo(
    () => options?.find((o) => o.id === selectedTemplateId),
    [options, selectedTemplateId],
  );

  const handleSavePreference = async (saved: boolean, id: string) => {
    try {
      if (!saved) return;

      // set Preference if not default template

      if (encounterType === "simple") {
        // if default template and no preference, don't save
        if (!preferredSimpleTemplate && id === medicalConfig.simpleTemplateId)
          return;

        // if preference is already set, don't save
        if (
          preferredSimpleTemplate?.id &&
          preferredDetailedTemplate.templateId === id
        )
          return;

        // save preference
        const res = await templateApi.saveTemplatePreference({
          type: "simple",
          templateId: id,
        });

        dispatch(setPreferredSimpleTemplate(res));
      } else {
        // if default template, don't save preference
        if (
          !preferredDetailedTemplate &&
          id === medicalConfig.detailedTemplateId
        )
          return;

        // if preference is already set, don't save
        if (
          preferredDetailedTemplate?.id &&
          preferredDetailedTemplate.templateId === id
        )
          return;

        // save preference
        const res = await templateApi.saveTemplatePreference({
          type: "detailed",
          templateId: id,
        });

        dispatch(setPreferredDetailedTemplate(res));
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={1.5} alignSelf="center">
        <FormLabel htmlFor="template-select">Template:</FormLabel>
      </Grid>
      <Grid item xs={7}>
        <Autocomplete
          disabled={disabled}
          fullWidth
          id="template-select"
          value={selected || ""}
          options={options}
          getOptionLabel={(o) => {
            // Value selected with enter, right from the input
            if (typeof o === "string") {
              return o;
            }

            if (
              [
                medicalConfig.simpleTemplateId,
                medicalConfig.detailedTemplateId,
              ].includes(o.id)
            ) {
              return `${o.title} (Default)`;
            }

            if (
              [
                preferredSimpleTemplate?.templateId,
                preferredDetailedTemplate?.templateId,
              ].includes(o.id)
            ) {
              return `${o.title} (Preferred)`;
            }

            // Regular option
            return o?.title || "";
          }}
          clearOnBlur
          isOptionEqualToValue={(o, v) => {
            if (typeof o === "string" || typeof v === "string") return false;
            return o?.id === v?.id;
          }}
          size={fieldSize}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Encounter Template" />
          )}
          autoHighlight
          onChange={(e, newValue) => {
            if (!newValue) {
              onTemplateSelect(null);
              handleSavePreference(futureUse, "");
              return;
            }

            if (typeof newValue === "string") {
              return;
            }

            if (newValue.id) {
              onTemplateSelect(newValue);
              handleSavePreference(futureUse, newValue.id);
            }
          }}
        />
      </Grid>
      <Grid item xs={3.5}>
        <FormControlLabel
          htmlFor="save-template-preference"
          control={
            <Checkbox
              checked={futureUse}
              id="save-template-preference"
              onChange={(e) => {
                setFutureUse(e.target.checked);
                handleSavePreference(e.target.checked, selected?.id || "");
              }}
            />
          }
          sx={{
            m: 0,
          }}
          label="Set as my default"
        />
      </Grid>
    </Grid>
  );
}
